import { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  getDividendsReceived,
  getGlobalDividendsReceived,
  DividendsResponse,
} from "../services/portfolio-service";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Loader2 } from "lucide-react";

interface DividendHistogramProps {
  bookId: string; // "global" for global mode
  currencyCode?: string;
  className?: string;
  contentClassName?: string;
}

// Color palette for different assets
const ASSET_COLORS = [
  "#3b82f6", // blue
  "#10b981", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#84cc16", // lime
  "#f97316", // orange
  "#6366f1", // indigo
];

export function DividendHistogram({
  bookId,
  currencyCode = "EUR",
  className,
  contentClassName,
}: DividendHistogramProps) {
  const [data, setData] = useState<DividendsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<"month" | "year">("month");
  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    undefined,
  );

  const isGlobal = bookId === "global";

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setData(null); // Reset data to prevent stale duplicates
      const response = isGlobal
        ? await getGlobalDividendsReceived(
            groupBy,
            groupBy === "month" ? selectedYear : undefined,
          )
        : await getDividendsReceived(
            bookId,
            groupBy,
            groupBy === "month" ? selectedYear : undefined,
          );
      setData(response);

      // Set default year to most recent available if not set
      if (!selectedYear && response.available_years.length > 0) {
        setSelectedYear(response.available_years[0]);
      }
      setLoading(false);
    };
    fetchData();
  }, [bookId, groupBy, selectedYear, isGlobal]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const formatPeriod = (period: string) => {
    if (groupBy === "year") {
      return period;
    }
    const [, month] = period.split("-");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return monthNames[parseInt(month) - 1] || period;
  };

  const resolveAssetName = (item: {
    asset_name?: string;
    asset_symbol?: string;
    asset_id?: string;
  }) => item.asset_name || item.asset_symbol || item.asset_id || "Asset";

  // Get unique assets and assign colors
  const { uniqueAssets, assetColorMap } = useMemo(() => {
    if (!data?.aggregations) return { uniqueAssets: [], assetColorMap: {} };

    const assets = new Set<string>();
    data.aggregations.forEach((agg) => {
      agg.by_asset?.forEach((item) => assets.add(resolveAssetName(item)));
    });

    const uniqueAssets = Array.from(assets);
    const assetColorMap: Record<string, string> = {};
    uniqueAssets.forEach((asset, i) => {
      assetColorMap[asset] = ASSET_COLORS[i % ASSET_COLORS.length];
    });

    return { uniqueAssets, assetColorMap };
  }, [data?.aggregations]);

  // Transform data for stacked bar chart - deduplicated by period
  const chartData = useMemo(() => {
    if (!data?.aggregations) return [];

    // Use a Map to deduplicate by period (keeps the last occurrence)
    const periodMap = new Map<string, Record<string, unknown>>();

    data.aggregations.forEach((agg) => {
      const assetTotals: Record<
        string,
        { name: string; amount: number; count: number }
      > = {};
      agg.by_asset?.forEach((item) => {
        const name = resolveAssetName(item);
        if (!assetTotals[name]) {
          assetTotals[name] = { name, amount: 0, count: 0 };
        }
        assetTotals[name].amount += item.amount;
        assetTotals[name].count += item.count;
      });

      const entry: Record<string, unknown> = {
        period: agg.period,
        total: agg.total_amount,
        _byAsset: Object.values(assetTotals),
      };

      // Add each asset as a separate key for stacking
      Object.values(assetTotals).forEach((item) => {
        entry[item.name] = item.amount;
      });

      // If this period already exists, merge the data (sum amounts)
      if (periodMap.has(agg.period)) {
        // This should not happen if backend is correct, but as a safety measure
        // we keep the latest entry (overwrites)
        periodMap.set(agg.period, entry);
      } else {
        periodMap.set(agg.period, entry);
      }
    });

    // Convert map back to array and sort by period
    return Array.from(periodMap.values()).sort((a, b) =>
      String(a.period).localeCompare(String(b.period)),
    );
  }, [data?.aggregations]);

  const totalDividends =
    data?.aggregations?.reduce((sum, d) => sum + d.total_amount, 0) || 0;
  const availableYears = data?.available_years || [];

  // Format Y-axis based on max value
  const maxValue = Math.max(
    ...(chartData.map((d) => d.total as number) || [0]),
  );
  const formatYAxis = (value: number) => {
    if (maxValue >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (maxValue >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value.toString();
  };

  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ payload: Record<string, unknown> }>;
    label?: string;
  }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const byAsset = data._byAsset as Array<{
      name: string;
      amount: number;
      count: number;
    }>;

    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs">
        <div className="font-medium mb-2">{formatPeriod(label || "")}</div>
        <div className="space-y-1">
          {byAsset?.map((item) => (
            <div key={item.name} className="flex justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: assetColorMap[item.name] }}
                />
                <span className="text-muted-foreground">
                  {item.name}
                </span>
              </div>
              <span className="font-mono">{formatCurrency(item.amount)}</span>
            </div>
          ))}
          <div className="border-t border-border pt-1 mt-1 flex justify-between font-medium">
            <span>Total</span>
            <span className="font-mono">
              {formatCurrency(data.total as number)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className={cn("bg-card border-border/40 flex flex-col", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Dividends Received
          </CardTitle>
        </CardHeader>
        <CardContent
          className={cn(
            "h-[200px] flex items-center justify-center",
            contentClassName,
          )}
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.aggregations || data.aggregations.length === 0) {
    return (
      <Card className={cn("bg-card border-border/40 flex flex-col", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Dividends Received
          </CardTitle>
        </CardHeader>
        <CardContent
          className={cn(
            "h-[200px] flex items-center justify-center",
            contentClassName,
          )}
        >
          <p className="text-sm text-muted-foreground">
            No dividend records found
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("bg-card border-border/40 flex flex-col", className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-medium">
            Dividends Received
          </CardTitle>
          <div className="flex items-center gap-2">
            {groupBy === "month" && availableYears.length > 0 && (
              <select
                value={selectedYear || ""}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="text-xs border border-border/40 rounded px-2 py-1 bg-background"
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            )}
            <div className="flex gap-1">
              <Button
                variant={groupBy === "month" ? "default" : "outline"}
                size="sm"
                className="h-6 text-xs px-2"
                onClick={() => setGroupBy("month")}
              >
                Monthly
              </Button>
              <Button
                variant={groupBy === "year" ? "default" : "outline"}
                size="sm"
                className="h-6 text-xs px-2"
                onClick={() => setGroupBy("year")}
              >
                Yearly
              </Button>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Total:{" "}
          <span className="font-medium text-foreground">
            {formatCurrency(totalDividends)}
          </span>
        </p>
      </CardHeader>
      <CardContent className={cn("h-[180px] flex-1", contentClassName)}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 5, left: -10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 10 }}
              tickFormatter={formatPeriod}
              className="text-muted-foreground"
              axisLine={{ stroke: "hsl(var(--border))" }}
              tickLine={{ stroke: "hsl(var(--border))" }}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              tickFormatter={formatYAxis}
              width={40}
              className="text-muted-foreground"
              axisLine={{ stroke: "hsl(var(--border))" }}
              tickLine={{ stroke: "hsl(var(--border))" }}
            />
            <Tooltip content={<CustomTooltip />} />
            {uniqueAssets.map((assetSymbol) => (
              <Bar
                key={assetSymbol}
                dataKey={assetSymbol}
                stackId="dividends"
                fill={assetColorMap[assetSymbol]}
                radius={
                  uniqueAssets.indexOf(assetSymbol) === uniqueAssets.length - 1
                    ? [4, 4, 0, 0]
                    : [0, 0, 0, 0]
                }
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
